import React, { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Four Get-Involved submission forms. All POST to /api/community/* which
 * writes a pending submission the admin can later moderate.
 *
 * Variants: `nominate_stop`, `on_route`, `road_dare`, `meet_booboo`
 * (`meet_booboo` is handled by the existing /meetups + /rsvp page — this form
 *  just drives traffic there, no new endpoint needed.)
 */

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const FORM_META = {
  nominate_stop: {
    title: "NOMINATE A WORTH THE STOP",
    body: "Tell BooBoo somewhere worth pulling over for.",
    endpoint: "/community/nominate-stop",
    submit: "SEND NOMINATION",
    testid: "nominate-stop",
  },
  on_route: {
    title: "I'M ON YOUR ROUTE",
    body: "Your city might land on the route.",
    endpoint: "/community/on-route",
    submit: "ADD ME TO THE ROUTE",
    testid: "on-route",
  },
  road_dare: {
    title: "ROAD DARE",
    body: "Suggest a fun, legal, safe FMAA road challenge.",
    endpoint: "/community/road-dare",
    submit: "SEND THE DARE",
    testid: "road-dare",
  },
};

export const CommunityForm = ({ variant }) => {
  const meta = FORM_META[variant];
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(meta.endpoint, form);
      setSubmitted(true);
      toast.success("Logged. BooBoo will see it.");
      setForm({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something broke. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="tac-card p-5 flex flex-col gap-3"
        data-testid={`community-form-${meta.testid}-success`}
      >
        <div className="road-sticker self-start">RECEIVED</div>
        <h3 className="font-display text-2xl uppercase">You're on the log.</h3>
        <p className="text-sm text-zinc-400">
          BooBoo reviews these personally. If it fits the road, you'll hear back.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="btn-ghost self-start text-[11px]"
          data-testid={`community-form-${meta.testid}-new`}
        >
          + Send another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="tac-card p-5 flex flex-col gap-3"
      data-testid={`community-form-${meta.testid}`}
    >
      <div>
        <h3 className="font-display text-2xl uppercase tracking-tight leading-none">
          {meta.title}
        </h3>
        <p className="text-xs font-mono-tactical text-zinc-500 mt-2 uppercase tracking-widest">
          {meta.body}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="Your name"
          className="tac-input"
          value={form.submitter_name || ""}
          onChange={set("submitter_name")}
          required
          data-testid={`${meta.testid}-name`}
        />
        <input
          type="email"
          placeholder="Email"
          className="tac-input"
          value={form.submitter_email || ""}
          onChange={set("submitter_email")}
          required
          data-testid={`${meta.testid}-email`}
        />
      </div>

      {variant === "nominate_stop" && (
        <>
          <input
            type="text"
            placeholder="Place name (e.g. Cadillac Ranch)"
            className="tac-input"
            value={form.place_name || ""}
            onChange={set("place_name")}
            required
            data-testid={`${meta.testid}-place`}
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="City"
              className="tac-input"
              value={form.city || ""}
              onChange={set("city")}
              data-testid={`${meta.testid}-city`}
            />
            <select
              className="tac-input"
              value={form.state_code || ""}
              onChange={set("state_code")}
              required
              data-testid={`${meta.testid}-state`}
            >
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <textarea
            placeholder="Why is this worth the stop?"
            className="tac-input"
            rows={3}
            value={form.why || ""}
            onChange={set("why")}
            required
            data-testid={`${meta.testid}-why`}
          />
        </>
      )}

      {variant === "on_route" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Your city"
              className="tac-input"
              value={form.city || ""}
              onChange={set("city")}
              required
              data-testid={`${meta.testid}-city`}
            />
            <select
              className="tac-input"
              value={form.state_code || ""}
              onChange={set("state_code")}
              required
              data-testid={`${meta.testid}-state`}
            >
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <textarea
            placeholder="Anything we should know? (optional)"
            className="tac-input"
            rows={3}
            value={form.note || ""}
            onChange={set("note")}
            data-testid={`${meta.testid}-note`}
          />
        </>
      )}

      {variant === "road_dare" && (
        <textarea
          placeholder="What's the dare?"
          className="tac-input"
          rows={4}
          value={form.dare || ""}
          onChange={set("dare")}
          required
          data-testid={`${meta.testid}-dare`}
        />
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-blaze self-start"
        data-testid={`${meta.testid}-submit`}
      >
        {submitting ? "SENDING..." : meta.submit}
      </button>
    </form>
  );
};
